import { FormProvider, SubmitHandler } from 'react-hook-form'
import { FormObjectType, GiftRefundFormItemsType } from 'src/types/react-hook-form'
import {
  giftDetailInfoByGiftIdxQueryOption,
  refundBankListQueryOption,
  useCustomQuery,
} from 'src/hooks/react-query/useCustomQuery.hook'
import { QueryClient } from '@tanstack/react-query'
import {
  giftRefundSubmitQueryOption,
  useCustomMutation,
} from 'src/hooks/react-query/useCustomMutation.hook'
import {
  BankListSpace,
  GiftDetailInfoByGiftIdxSpace,
  GiftRefundSpace,
  GiftType,
  PassCertResponseSpace,
  PassReqType,
  ResponseDataType,
} from 'src/types/api'
import React, { useEffect, useState } from 'react'
import { useModal } from 'src/provider/ModalContextProvider'
import useParamsType, { MatchParams } from 'src/hooks/useParamsType.hook'
import usePageNav from 'src/hooks/usePageNav.hook'
import RenderIf from 'src/components/common/RenderIf'
import { Loading } from 'src/components/common/Loading'
import usePassCert from 'src/hooks/usePassCert.hook'
import { v4 } from 'uuid'
import { DateUtils } from 'src/utils/date'
import GiftRefundPageView from 'src/pages/GiftRefundPage/view'
import useGiftRefundForm from 'src/hooks/react-hook-form/useGiftRefundForm.hook'
import { Params } from 'react-router-dom'
import Modal from 'src/components/common/Modal'
import { HyphenReplace } from 'src/utils/regExps_util'
import { ERRCODE_SUCCESS } from 'src/service/constant/errcodes'
import GiftRefundInfoConfirmModal from 'src/components/common/Modal/children/GiftRefundInfoConfirmModal'

export interface GiftRefundPageProps {
  registerAttribute: Record<keyof GiftRefundFormItemsType, Partial<FormObjectType>>
  refundSubmitRequestHandler: SubmitHandler<GiftRefundFormItemsType>
  passCertRequest: (type: PassReqType, internalOpen: boolean) => void
  giftDetailData: GiftDetailInfoByGiftIdxSpace.Response
  percentage: number
}

export const giftRefundPageLoader =
  (queryClient: QueryClient) =>
  async ({
    params: { giftIdx },
    request,
  }: {
    params: Params<keyof MatchParams>
    request: Request
  }) => {
    const dataQueryValues = [
      await queryClient.ensureQueryData(refundBankListQueryOption()),
      await queryClient.ensureQueryData(
        giftDetailInfoByGiftIdxQueryOption({ GiftIdx: Number(giftIdx), GiftURL: request.url }),
      ),
    ]
    return Promise.allSettled(dataQueryValues)
  }

const GiftRefundPage = () => {
  const [uuid, setUuid] = useState(v4())
  const { giftIdx, requestId, giftType } = useParamsType()
  const { isOpen: modalOpen, openModal } = useModal()
  const { giftcardPageNav } = usePageNav()
  const { isValidInDays, isValidInYears } = DateUtils
  const { passCertRequest, passCertResponse } = usePassCert(uuid)

  const { data: bankListQueryData, isLoading: isBankListLoading } =
    useCustomQuery<BankListSpace.Response>(refundBankListQueryOption())

  const { data: giftDetailByGiftIdxQueryData, isLoading: isGiftDetailByGiftIdxLoading } =
    useCustomQuery<GiftDetailInfoByGiftIdxSpace.Response>(
      giftDetailInfoByGiftIdxQueryOption({
        GiftIdx: Number(giftIdx),
        GiftURL: window.location.href,
      }),
    )

  const { mutateAsync: refundMutate, isPending: refundPending } = useCustomMutation<
    GiftRefundSpace.Request,
    GiftRefundSpace.Response
  >(giftRefundSubmitQueryOption())

  const { PurchaseDT, RecipientPNumber, GiftStatus, SalePrice } =
    giftDetailByGiftIdxQueryData?.Body!
  const percentage = isValidInDays(PurchaseDT, 365) ? 1 : 0.9
  const { methods, registerAttribute } = useGiftRefundForm(HyphenReplace(RecipientPNumber))

  const refundSubmit = (data: GiftRefundFormItemsType) => {
    const { name, pNumber, bankNum, accountNumber } = data
    const submitData = {
      GiftIdx: Number(giftIdx),
      RecipientPNumber: pNumber.replaceAll('-', ''),
      PassName: name,
      BankNum: Number(bankNum),
      AccountNumber: accountNumber,
    }
    refundMutate(submitData).then(({ Header, Body }) => {
      const { ErrCode } = Header
      if (ErrCode === ERRCODE_SUCCESS) {
        openModal({
          contents: {
            t: '환불이 접수되었습니다.',
            d: '환불은 영업일 5~7일 이내 처리됩니다.',
          },
          button: {
            submit: {
              t: '확인',
              f: () => giftcardPageNav(requestId, Number(giftType) as GiftType, Number(giftIdx)),
            },
          },
        })
      }
    })
  }

  const refundSubmitRequestHandler: SubmitHandler<GiftRefundFormItemsType> = (data) => {
    const { name, bankNum, accountNumber } = data

    const selectedBankName = bankListQueryData?.Body.BankInfo.find(
      ({ BankNum }: BankListSpace.BankList) => BankNum === Number(bankNum),
    )?.BankName as string

    openModal({
      contents: {
        t: '환불 정보 확인',
        children: (
          <GiftRefundInfoConfirmModal
            refundInfoText={`${name}\n${accountNumber}(${selectedBankName})`}
            price={SalePrice * percentage}
          />
        ),
      },
      button: {
        direction: 'column',
        cancel: { t: '수정하기' },
        submit: { t: '환불신청', f: () => refundSubmit(data) },
      },
    })
  }

  useEffect(() => {
    window.scrollTo({ top: 0 })

    handlePassCertResponse()

    const isUsableMine = [1, 7].includes(GiftStatus)
    // const isAlwaysCoupon = CouponType === 0
    const isCancelAble = isValidInYears(PurchaseDT, 5)
    if (!(isUsableMine && isCancelAble))
      giftcardPageNav(requestId, Number(giftType) as GiftType, Number(giftIdx))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const handlePassCertResponse = () => {
    const successFn = (res: ResponseDataType<PassCertResponseSpace.Response>) => {
      const { Name, PNumber } = res.Body
      setFormCertValues(Name, PNumber)
    }
    const failFn = () => setUuid(v4())
    passCertResponse(1, successFn, failFn)
  }

  const setFormCertValues = (name: string, pNumber?: string) => {
    if (!!name) {
      methods.clearErrors('name')
      methods.setValue('name', name)
    }
    if (!!pNumber) {
      methods.setValue('pNumber', HyphenReplace(pNumber))
      methods.trigger('pNumber')
    }
  }

  const props: GiftRefundPageProps = {
    registerAttribute,
    refundSubmitRequestHandler,
    passCertRequest,
    giftDetailData: giftDetailByGiftIdxQueryData?.Body!,
    percentage,
  }

  return (
    <FormProvider {...methods}>
      <RenderIf when={modalOpen}>
        <Modal />
      </RenderIf>
      <RenderIf when={isBankListLoading || isGiftDetailByGiftIdxLoading || refundPending}>
        <Loading.Default />
      </RenderIf>
      <GiftRefundPageView {...props} />
    </FormProvider>
  )
}
export default GiftRefundPage
